import * as React from 'react';

import styled from '@emotion/styled';
import Card from '@lightspeed/cirrus-card';
import {
  NoDataTd,
  Sortable,
  Table,
  TableActions,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@lightspeed/cirrus-table';
import { Bone } from '@lightspeed/flame/Bone';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { IconCheckmark } from '@lightspeed/flame/Icon/Checkmark';
import { Heading2, Text } from '@lightspeed/flame/Text';

import { OrderLineRow } from 'root/apps/PurchaseOrder/NuorderOrdering/components/OrderLineRow';
import { OrderLineTabs } from 'root/apps/PurchaseOrder/NuorderOrdering/components/OrderLineTabs';
import { SearchBar } from 'root/apps/PurchaseOrder/NuorderOrdering/components/subcomponents/SearchBar';
import {
  LOG_SOURCE,
  useNuorderOrdering,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Context';
import { useUpdateSortingPreference } from 'root/apps/PurchaseOrder/NuorderOrdering/context/Queries';
import {
  Counts,
  DefaultSortingPreference,
  ItemReadiness,
  RowData,
  RowDataSortable,
  TableFilter,
} from 'root/apps/PurchaseOrder/NuorderOrdering/types';
import {
  computeIsReadOnly,
  computeItemReadiness,
  computeNuorderAvailability,
  getItemB2bCatalogUUID,
  getItemVendorNum,
} from 'root/apps/PurchaseOrder/NuorderOrdering/utils/dataUtils';
import { applyFilters, applySort } from 'root/apps/PurchaseOrder/NuorderOrdering/utils/tableUtils';
import { logError } from 'root/datadog/datadogLogs';

const TrThStyled = styled(Tr)`
  th:nth-of-type(7) {
    display: flex;
    justify-content: flex-end;
    text-align: right !important;
  }
`;

interface OrderLineTableProps {
  testHelper?: (dataSortable: RowDataSortable[], counts: Counts) => void;
}

export const OrderLineTable: React.FC<OrderLineTableProps> = ({ testHelper }) => {
  const {
    state: {
      purchase,
      purchaseLines,
      items,
      nuorderProductAvailability,
      sortingPreference,
      nuorderOrder,
    },
  } = useNuorderOrdering();

  const { getSortingDirection, toggleSort, sortKey, sortDirection, setSort } = Sortable.useSortable(
    DefaultSortingPreference.sortKey,
    DefaultSortingPreference.sortDirection
  );

  const [rows, setRows] = React.useState<RowDataSortable[]>([]);
  const [data, setData] = React.useState<RowData[]>([]);
  const [filters, setFilters] = React.useState<TableFilter<RowDataSortable>[]>([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sortingPreferenceInitialized, setSortingPreferenceInitialized] = React.useState(false);

  const mutationUpdateSortingPreference = useUpdateSortingPreference();

  const isReadOnly = React.useMemo(() => {
    return computeIsReadOnly(purchase, nuorderOrder);
  }, [nuorderOrder, purchase]);

  React.useEffect(() => {
    if (purchase && purchaseLines && items) {
      setData(
        purchaseLines
          .filter(({ itemID }) => items[itemID])
          .map((line) => {
            const item = items[line.itemID];
            return {
              id: line.orderLineID,
              purchaseID: line.orderID,
              itemID: item.itemID,
              vendorID: purchase.vendorID,
              upc: item.upc,
              description: item.description,
              itemVendorNum: getItemVendorNum(item, purchase.vendorID),
              b2bCatalogUUID: getItemB2bCatalogUUID(item, purchase.vendorID),
              qty: line.quantity,
              total: line.total,
            };
          })
      );
    }
  }, [purchase, purchaseLines, items]);

  const dataSortable: RowDataSortable[] = React.useMemo(() => {
    return data.map((row) => {
      const availability = computeNuorderAvailability(
        row.itemID,
        row.vendorID,
        items,
        nuorderProductAvailability
      );
      const itemReadiness = computeItemReadiness(
        row.upc,
        row.b2bCatalogUUID,
        row.qty,
        availability,
        nuorderProductAvailability
      );
      return {
        ...row,
        availability,
        itemReadiness,
      };
    });
  }, [data, items, nuorderProductAvailability]);

  const counts = React.useMemo(() => {
    return dataSortable.reduce(
      (acc, row) => {
        switch (row.itemReadiness) {
          case ItemReadiness.NO_UPC:
          case ItemReadiness.NO_UUID:
          case ItemReadiness.NOT_ON_NUORDER:
            acc.error += 1;
            break;
          case ItemReadiness.NO_AVAILABILITY:
          case ItemReadiness.OUT_OF_STOCK:
          case ItemReadiness.OVER_ORDERED:
            acc.warning += 1;
            break;
          case ItemReadiness.NOT_READY:
          case ItemReadiness.READY:
            acc.ready += 1;
            break;
          default:
            break;
        }
        acc.all += 1;
        return acc;
      },
      { all: 0, error: 0, warning: 0, ready: 0 }
    );
  }, [dataSortable]);

  React.useEffect(() => {
    if (testHelper) {
      testHelper(rows, counts);
    }
  }, [rows, counts, testHelper]);

  const isLoading = React.useMemo(() => {
    return purchase === null || purchaseLines === null || items === null;
  }, [purchase, purchaseLines, items]);

  /**
   * Apply filtering then sorting to the table data.
   */
  React.useEffect(() => {
    setRows(
      applySort<RowDataSortable>(
        sortKey as keyof RowData,
        sortDirection,
        applyFilters<RowDataSortable>(
          ['upc', 'description', 'itemVendorNum'],
          searchTerm,
          filters,
          dataSortable
        )
      )
    );
  }, [dataSortable, sortDirection, sortKey, searchTerm, filters, setRows]);

  /**
   * Load previously stored sorting preference if different from default values.
   */
  React.useEffect(() => {
    if (!sortingPreference) return;
    if (sortingPreferenceInitialized) return;
    if (
      sortingPreference.sortKey === sortKey &&
      sortingPreference.sortDirection === sortDirection
    ) {
      return;
    }
    setSort(sortingPreference.sortKey, sortingPreference.sortDirection);
    setSortingPreferenceInitialized(true);
  }, [sortingPreference, sortingPreferenceInitialized, sortKey, sortDirection, setSort]);

  /**
   * Update stored sorting preferences when sortKey or sortDirection changes.
   */
  React.useEffect(() => {
    if (!sortingPreference || !sortKey || !sortDirection) return;
    if (mutationUpdateSortingPreference.isLoading) return;
    if (
      sortingPreference.sortKey === sortKey &&
      sortingPreference.sortDirection === sortDirection
    ) {
      return;
    }
    mutationUpdateSortingPreference.mutate({
      sortKey,
      sortDirection,
    });
    setSortingPreferenceInitialized(true);
  }, [sortKey, sortDirection, sortingPreference, mutationUpdateSortingPreference]);

  /**
   * Handle errors in the mutation
   */
  React.useEffect(() => {
    if (mutationUpdateSortingPreference.error) {
      logError(mutationUpdateSortingPreference.error, {
        source: LOG_SOURCE,
        file: 'OrderLineTable.tsx',
      });
    }
  }, [mutationUpdateSortingPreference.error]);

  return (
    <>
      <Heading2>
        <Text fontSize="large">
          {isLoading ? (
            <Bone width={300} height={30} style={{ marginBottom: '0.5em' }} />
          ) : (
            `Items to Order`
          )}
        </Text>
      </Heading2>
      <Card>
        {!isReadOnly && (
          <OrderLineTabs onSetFilters={setFilters} counts={counts} isLoading={isLoading} />
        )}
        <TableActions.ActionsBar
          leftHandActions={
            isLoading ? (
              <Bone width={400} height={30} data-test="bonesSearchBar" />
            ) : (
              <SearchBar onSearch={setSearchTerm} data-test="searchBar" />
            )
          }
          rightHandActions={<Box />}
        />
        <Divider />
        <Table>
          <Thead>
            {!isLoading && (
              <TrThStyled>
                {!isReadOnly && (
                  <Sortable.Th
                    direction={getSortingDirection('itemReadiness')}
                    onClick={() => toggleSort('itemReadiness')}
                    data-test="itemReadiness"
                  >
                    <IconCheckmark />
                  </Sortable.Th>
                )}
                <Sortable.Th
                  direction={getSortingDirection('upc')}
                  onClick={() => toggleSort('upc')}
                  data-test="upc"
                >
                  UPC
                </Sortable.Th>
                <Sortable.Th
                  direction={getSortingDirection('description')}
                  onClick={() => toggleSort('description')}
                  data-test="description"
                >
                  Item Description
                </Sortable.Th>
                <Sortable.Th
                  direction={getSortingDirection('itemVendorNum')}
                  onClick={() => toggleSort('itemVendorNum')}
                  data-test="itemVendorNum"
                >
                  Vendor ID
                </Sortable.Th>
                {!isReadOnly && (
                  <Sortable.Th
                    direction={getSortingDirection('availability')}
                    onClick={() => toggleSort('availability')}
                    data-test="availability"
                  >
                    Vendor QOH
                  </Sortable.Th>
                )}
                <Sortable.Th
                  direction={getSortingDirection('qty')}
                  onClick={() => toggleSort('qty')}
                  data-test="qty"
                >
                  Qty.
                </Sortable.Th>
                <Sortable.Th
                  direction={getSortingDirection('total')}
                  onClick={() => toggleSort('total')}
                  data-test="total"
                  // ...rest are applied to child button component
                  style={{ float: 'right' }}
                >
                  Unit Cost
                </Sortable.Th>
                <Th />
              </TrThStyled>
            )}
          </Thead>
          <Tbody>
            {/* eslint-disable-next-line no-nested-ternary */}
            {isLoading ? (
              [1, 2, 3, 4, 5].map((value) => (
                <Tr key={`skeleton_${value}`} data-test="bones">
                  {!isReadOnly && (
                    <Td>
                      <Bone />
                    </Td>
                  )}
                  <Td>
                    <Bone />
                  </Td>
                  <Td>
                    <Bone />
                  </Td>
                  <Td>
                    <Bone />
                  </Td>
                  {!isReadOnly && (
                    <Td>
                      <Bone />
                    </Td>
                  )}
                  <Td>
                    <Bone />
                  </Td>
                  <Td>
                    <Bone />
                  </Td>
                  <Td>
                    <Bone />
                  </Td>
                </Tr>
              ))
            ) : rows.length ? (
              rows.map((row) => <OrderLineRow key={row.id} data={row} isReadOnly={isReadOnly} />)
            ) : (
              <Tr>
                <NoDataTd colSpan={7} data-test="empty-state">
                  <Flex paddingY={6} width="100%" justifyContent="center" flexDirection="column">
                    <img
                      style={{ width: '250px', alignSelf: 'center' }}
                      alt="empty table"
                      src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/empty-table.svg"
                    />
                    <Text size="normal" fontWeight="700" paddingTop={4}>
                      No items found.
                    </Text>
                  </Flex>
                </NoDataTd>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Card>
    </>
  );
};
